import { useState, type CSSProperties, type ReactNode } from "react";
import type { CatalogCard } from "../../api/client";
import { Card } from "../Card";
import { catalogToCardView } from "../../logic/catalogToCardView";
import { pixelTheme } from "../../theme/pixel";
import { E32 } from "../../theme/endesga32";

interface InfinityFanHeroProps {
  uncommon: CatalogCard | null;
  legendary: CatalogCard | null;
  onCardClick?: (card: CatalogCard) => void;
}

// The hero illustration: a faint figure-8 SVG with a butter-coloured
// spark traveling its path, plus two fanned cards drifting on
// independent float keyframes. Hovering the fan spreads the cards
// outwards.
//
// Layering is three deep on purpose:
//   1. outer absolute position (`baseLeft` / `baseRight`)
//   2. middle div carries the rotation + the hover transform (these
//      need to live together so the hover transition composes with
//      the resting rotation)
//   3. inner div carries the float keyframe (translateY only).
// Stacking keeps each layer's transform from clobbering the next:
// CSS only applies one `transform` per element, so the keyframe on
// the inner layer animates without overwriting the outer rotation.
export function InfinityFanHero({
  uncommon,
  legendary,
  onCardClick,
}: InfinityFanHeroProps) {
  const [hover, setHover] = useState(false);

  const fanWidth = 430;
  const fanHeight = 380;
  const cardWidth = 206;

  const wrap: CSSProperties = {
    position: "relative",
    minHeight: 420,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const fan: CSSProperties = {
    position: "relative",
    zIndex: 2,
    width: fanWidth,
    height: fanHeight,
  };

  // Layer 1 — outer positioning. No transform here so the hover state
  // owns the rotation layer cleanly.
  const baseLeft: CSSProperties = {
    position: "absolute",
    left: 6,
    top: 54,
    width: cardWidth,
    zIndex: 2,
  };
  const baseRight: CSSProperties = {
    position: "absolute",
    right: 6,
    top: 18,
    width: cardWidth,
    zIndex: 3,
  };

  // Layer 2 — rotation + hover spread. Inline `transform` so the
  // smooth transition between rest and hover composes cleanly.
  const rotLeft: CSSProperties = {
    transform: hover
      ? "rotate(-11deg) translate(-10px, -4px)"
      : "rotate(-7deg)",
    transition: "transform 200ms cubic-bezier(.2,.9,.2,1)",
    transformOrigin: "center center",
  };
  const rotRight: CSSProperties = {
    transform: hover
      ? "rotate(10deg) translate(10px, -6px)"
      : "rotate(6deg)",
    transition: "transform 200ms cubic-bezier(.2,.9,.2,1)",
    transformOrigin: "center center",
  };

  return (
    <div style={wrap}>
      <svg
        viewBox="0 0 100 50"
        fill="none"
        stroke={E32.amber}
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{
          position: "absolute",
          width: "118%",
          left: "-9%",
          top: "50%",
          transform: "translateY(-50%)",
          opacity: 0.16,
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <path
          id="doi-landing-infpath"
          d="M 25 25 C 25 5, 50 5, 50 25 C 50 45, 75 45, 75 25 C 75 5, 50 5, 50 25 C 50 45, 25 45, 25 25 Z"
        />
        <circle
          r={2.4}
          fill={E32.butter}
          stroke="none"
          style={{ filter: `drop-shadow(0 0 6px ${E32.amber})` }}
        >
          <animateMotion dur="4.5s" repeatCount="indefinite" rotate="auto">
            <mpath href="#doi-landing-infpath" />
          </animateMotion>
        </circle>
      </svg>

      <div
        style={fan}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div style={baseLeft}>
          <div style={rotLeft}>
            <div className="doi-landing-float-a">
              <HeroCardSlot card={uncommon} onClick={onCardClick} />
            </div>
          </div>
        </div>
        <div style={baseRight}>
          <div style={rotRight}>
            <div className="doi-landing-float-b">
              <HeroCardSlot card={legendary} onClick={onCardClick} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroCardSlot({
  card,
  onClick,
}: {
  card: CatalogCard | null;
  onClick?: (card: CatalogCard) => void;
}) {
  if (!card) return <HeroCardPlaceholder />;
  const interactive = !!onClick;
  return (
    <ClickWrap
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={() => onClick?.(card)}
      onKeyDown={(ev) => {
        if ((ev.key === "Enter" || ev.key === " ") && onClick) {
          ev.preventDefault();
          onClick(card);
        }
      }}
      style={{ cursor: interactive ? "pointer" : "default" }}
    >
      <Card card={catalogToCardView(card)} size="normal" theme={pixelTheme} />
    </ClickWrap>
  );
}

function ClickWrap({
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { children: ReactNode }) {
  return <div {...rest}>{children}</div>;
}

function HeroCardPlaceholder() {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 206,
        height: 288,
        background: pixelTheme.panelSolid,
        border: `2px dashed ${pixelTheme.panelEdge}`,
        boxShadow: `4px 4px 0 0 ${E32.ink}`,
      }}
    />
  );
}
